import React, {useEffect, useState} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';

import {AutoSizedTextArea} from 'app/components/general/AutoSizedTextArea';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {
  createFormErrorsContext,
  FormControlGroup,
  type FormErrorsProps,
} from 'app/general/forms';
import StateButton from 'app/general/widgets/StateButton';
import {DOCUMENTS_HOME} from 'app/urls';
import {useCreateUser, useUsers} from 'app/user/api';
import {UserListItem} from 'app/user/components/UsersDashboard/UserListItem';
import {gt, ngt} from 'app/utils/i18n';

import EmailPreview from './EmailPreview';
import {WelcomeStructure} from './WelcomeStructure';

import type {WithRouterProps} from 'react-router';

export const InviteDashboard = (props: WithRouterProps) => {
  return (
    <WelcomeStructure {...props} currentStep={2}>
      {({onNext}) => <SweetSuperChargeInvitations onNext={onNext} {...props} />}
    </WelcomeStructure>
  );
};

type Props = {
  onNext: (location: string) => unknown;
};

export const SweetSuperChargeInvitations = ({
  onNext,
}: Props & WithRouterProps) => {
  const {data: users} = useUsers({
    page_size: '5',
    page: '1',
    ordering: '-date_joined',
  });

  const [templateName, setName] = useState<string | undefined>();
  const [templateEmail, setEmail] = useState<string | undefined>();
  const [subject, setSubject] = useState<string | undefined>();
  const [welcome_message, setMessage] = useState<string>(
    gt(
      'You are invited to join SweetProcess, a new platform where you will be able to read our policy and procedure documentation.'
    )
  );

  const isJustOwner = (users?.count || 0) <= 1;

  return (
    <>
      <div className="row">
        <div className="col">
          <p className="lead">
            {gt(
              'Invite key members of your team so they can help you create procedures and organize your account.'
            )}
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <NewMemberRow
            onChangeEmail={setEmail}
            onChangeName={setName}
            onChangeSubject={setSubject}
            onChangeMessage={setMessage}
            initialSubject={subject}
            initialWelcome={welcome_message}
          />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <ul className="list-group mt-4">
            {users?.results.map((user) => (
              <UserListItem
                key={user.url}
                user={user}
                pageContext="invite-dashboard"
              />
            ))}
          </ul>
        </div>
        <EmailPreview
          templateEmail={templateEmail}
          templateName={templateName}
          subject={subject}
          welcome_message={welcome_message}
          setSubject={setSubject}
        />
      </div>

      <div className="row mt-4 justify-content-end">
        <div className="col-auto">
          <LazyTooltip
            tooltip={gt(
              'Start using SweetProcess without inviting any teammates.'
            )}
          >
            <button
              className="btn text-secondary btn-link mr-2"
              onClick={() => onNext(DOCUMENTS_HOME)}
            >
              {gt('Skip')}
            </button>
          </LazyTooltip>
          <StateButton
            disabled={isJustOwner}
            busyText={gt('Setting up account...')}
            className="btn btn btn-primary"
            onClick={() => {
              onNext(DOCUMENTS_HOME);
            }}
          >
            {ngt('Add Teammate', 'Add Teammates', users?.count || 0)}
          </StateButton>
        </div>
      </div>
    </>
  );
};

interface NewMemberProps extends FormErrorsProps {
  onChangeName: (name: string) => unknown;
  onChangeEmail: (email: string) => unknown;
  onChangeSubject: (subject: string) => unknown;
  onChangeMessage: (message: string) => unknown;
  initialSubject?: string;
  initialWelcome: string;
}

const NewMemberRow = createFormErrorsContext<NewMemberProps>(
  ({
    captureFormErrorsFromRejection,
    setFormErrors,
    onChangeEmail,
    onChangeName,
    onChangeSubject,
    onChangeMessage,
    initialSubject,
    initialWelcome,
  }: NewMemberProps) => {
    const [name, setName] = useState<string | undefined>(undefined);
    const [email, setEmail] = useState<string | undefined>(undefined);
    const [welcome_subject, setSubject] = useState(initialSubject);
    const [welcome_message, setMessage] = useState(initialWelcome);

    const [createUser] = useCreateUser();

    useEffect(() => {
      if (welcome_subject == null) {
        setSubject(initialSubject);
      }
    }, [initialSubject]);

    const handleCreateUser = (e) => {
      e.preventDefault();
      if (name && email) {
        createUser({name, email, welcome_subject, welcome_message})
          .unwrap()
          .then(
            () => {
              setName(undefined);
              setEmail(undefined);
              setFormErrors({});
            },
            (err) => {
              captureFormErrorsFromRejection(err);
              throw err;
            }
          );
      }
    };

    return (
      <div
        className="row border-bottom py-2 d-flex justify-content-center"
        style={{position: 'relative'}}
      >
        <StylishUserForm className="form p-4">
          <div className="form-row px-3">
            <FormControlGroup key="name" className="mb-0 col-5" field="name">
              {(invalidFeedback, errorClass) => (
                <div className="input-group">
                  <div className="input-group-prepend">
                    <span
                      className={`input-group-text bg-white pr-0 ${errorClass}`}
                    >
                      <i className="fa fa-fw fa-user" />
                    </span>
                  </div>
                  <input
                    type="text"
                    className={`form-control border-left-0 ${errorClass}`}
                    name={'name'}
                    onChange={(e) => {
                      setName(e.target.value);
                      onChangeName(e.target.value);
                    }}
                    value={name || ''}
                    placeholder={gt('Name')}
                    autoFocus={true}
                  />
                  {invalidFeedback}
                </div>
              )}
            </FormControlGroup>

            <FormControlGroup key="email" className="mb-0 col-5" field="email">
              {(invalidFeedback, errorClass) => (
                <div className="input-group">
                  <div className="input-group-prepend">
                    <span
                      className={`input-group-text bg-white pr-0 ${errorClass}`}
                    >
                      <i className="fa fa-fw fa-envelope" />
                    </span>
                  </div>
                  <input
                    type="text"
                    className={`form-control border-left-0 ${errorClass}`}
                    autoFocus={false}
                    name={'email'}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      onChangeEmail(e.target.value);
                    }}
                    value={email || ''}
                    placeholder={gt('Email')}
                  />
                  {invalidFeedback}
                </div>
              )}
            </FormControlGroup>
            <div className="col-2 text-right">
              <button
                type="submit"
                disabled={name == null || email == null}
                className="btn btn-primary px-4"
                onClick={handleCreateUser}
              >
                {gt('Invite')}
              </button>
            </div>
          </div>
          <FormControlGroup
            key="subject"
            className="mb-0 col-12 mt-2"
            field="subject"
          >
            <div className="input-group">
              <input
                type="text"
                className={`form-control`}
                autoFocus={false}
                name={'subject'}
                onChange={(event) => {
                  setSubject(event.target.value);
                  onChangeSubject(event.target.value);
                }}
                value={welcome_subject ?? ''}
                placeholder={gt('Welcome Email Subject')}
              />
            </div>
          </FormControlGroup>
          <FormControlGroup
            key="message"
            className="mb-0 col-12 mt-2"
            field="message"
          >
            <div className="input-group">
              <AutoSizedTextArea
                id="message"
                placeholder={gt('Welcome Email Message')}
                className="form-control"
                value={welcome_message}
                onChange={(value: string) => {
                  setMessage(value);
                  onChangeMessage(value);
                }}
                onBlur={() => setMessage(welcome_message)}
              />
            </div>
          </FormControlGroup>
        </StylishUserForm>
      </div>
    );
  }
);

const StylishUserForm = styled.form`
  background: ${colors.lightBg};
  border-radius: 0.25em;
`;
